import { ArrowDownLeft, ArrowUpRight, Clock3, Lock, NotebookPen, Plus, Scale, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CategoryDonut } from './components/Charts'
import { CreditCardsSection } from './components/credit/CreditCardsSection'
import { LoansSection } from './components/credit/LoansSection'
import { ReminderSettingsButton } from './components/credit/ReminderSettings'
import { OWNER_LOAN_PERMS } from './loans'
import { Kpi, Money, PeriodSelect, Section } from './components/FinanceBits'
import { usePersonalSummary } from './personal/api'
import { EntryForm } from './personal/components/EntryForm'
import { NoteForm } from './personal/components/NoteForm'
import { EntriesList, NotesGrid, WalletsGrid } from './personal/components/PersonalTabs'
import { SalarySyncCard } from './personal/components/SalarySyncCard'
import { WalletForm } from './personal/components/WalletForm'
import { PERSONAL_EXPENSE_CATEGORIES, type PersonalEntry, type PersonalNote, type PersonalWallet } from './personal/types'
import { monthLabel, usePeriod } from './ui'

const TABS = ['summary', 'entries', 'wallets', 'cards', 'loans', 'advances', 'notes'] as const
type Tab = (typeof TABS)[number]

/** Yalnız oturum sahibine özel kişisel finans; şirket kayıtlarıyla hiçbir veri paylaşılmaz. */
export function PersonalFinancePage() {
  const { t } = useTranslation()
  const { month, setMonth } = usePeriod()
  const summary = usePersonalSummary(month)
  const [tab, setTab] = useState<Tab>('summary')
  const [kind, setKind] = useState<'all' | 'income' | 'expense'>('all')
  const [entry, setEntry] = useState<{ open: boolean; value: PersonalEntry | null }>({ open: false, value: null })
  const [note, setNote] = useState<{ open: boolean; value: PersonalNote | null }>({ open: false, value: null })
  const [wallet, setWallet] = useState<{ open: boolean; value: PersonalWallet | null }>({ open: false, value: null })
  const s = summary.data

  const openEntry = (value: PersonalEntry | null = null) => setEntry({ open: true, value })
  const openNote = (value: PersonalNote | null = null) => setNote({ open: true, value })

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.personal.title')}
        description={t('finance.personal.subtitle')}
        actions={
          <>
            <PeriodSelect value={month} onChange={setMonth} />
            <ReminderSettingsButton scope="personal" size="default" />
            <Button variant="outline" onClick={() => openNote()}>
              <NotebookPen />
              {t('finance.personal.addNote')}
            </Button>
            <Button onClick={() => openEntry()}>
              <Plus />
              {t('finance.personal.addEntry')}
            </Button>
          </>
        }
      />

      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/[0.05] p-4 text-sm">
          <Lock className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
          <p>
            <span className="font-semibold">{t('finance.personal.privateTitle')}</span> <span className="text-muted-foreground">{t('finance.personal.privateHint')}</span>
          </p>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            {TABS.map((k) => (
              <TabsTrigger key={k} value={k}>
                {t(`finance.personal.tabs.${k}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {tab === 'summary' ? (
          summary.isError ? (
            <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
          ) : !s ? (
            <Skeleton className="h-64 rounded-xl" />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Kpi icon={ArrowDownLeft} tone="success" label={t('finance.personal.income')} value={<Money value={s.income} />} footer={monthLabel(month)} />
                <Kpi icon={ArrowUpRight} tone="danger" label={t('finance.personal.expense')} value={<Money value={s.expense} />} footer={monthLabel(month)} />
                <Kpi icon={Scale} tone="info" label={t('finance.personal.net')} value={<Money value={s.net} signed />} footer={t('finance.personal.netHint')} />
                <Kpi icon={Clock3} tone="warning" label={t('finance.personal.pending')} value={<Money value={s.pending.amount} />} footer={t('finance.personal.pendingHint', { count: s.pending.count })} />
              </div>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_22rem]">
                <Section title={t('finance.personal.byCategory')}>
                  <CategoryDonut
                    total={s.expense}
                    centerLabel={t('finance.personal.expense')}
                    slices={s.byCategory.map((c) => ({ id: c.category, name: t(`finance.personal.category.${c.category}`), amount: c.amount, pct: c.pct, color: (PERSONAL_EXPENSE_CATEGORIES as readonly string[]).indexOf(c.category) + 1 }))}
                  />
                </Section>
                <Section title={t('finance.personal.recent')} bodyClassName="p-0" actions={<Button size="sm" variant="ghost" onClick={() => setTab('entries')}>{t('finance.personal.seeAll')}</Button>}>
                  <EntriesList kind="all" month={month} onEdit={openEntry} />
                </Section>
                <div className="space-y-4">
                  <Section title={t('finance.personal.walletsTotal')} icon={Wallet}>
                    <p className="text-2xl font-semibold"><Money value={s.walletsTotal} /></p>
                    <Button size="sm" variant="link" className="h-auto p-0" onClick={() => setTab('wallets')}>
                      {t('finance.personal.manageWallets')}
                    </Button>
                  </Section>
                  <Section title={t('finance.personal.pinnedNotes')} icon={NotebookPen}>
                    <NotesGrid onEdit={openNote} pinnedOnly />
                  </Section>
                </div>
              </div>
            </div>
          )
        ) : tab === 'entries' ? (
          <Section
            title={t('finance.personal.tabs.entries')}
            bodyClassName="p-0"
            actions={
              <Tabs value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
                <TabsList size="sm">
                  {(['all', 'expense', 'income'] as const).map((k) => (
                    <TabsTrigger key={k} value={k}>
                      {t(`finance.personal.filter.${k}`)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            }
          >
            <EntriesList kind={kind} month={null} onEdit={openEntry} />
          </Section>
        ) : tab === 'cards' ? (
          <CreditCardsSection scope="personal" canWrite />
        ) : tab === 'loans' ? (
          <LoansSection scope="personal" perms={OWNER_LOAN_PERMS} />
        ) : tab === 'advances' ? (
          <LoansSection kind="cash_advance" scope="personal" perms={OWNER_LOAN_PERMS} />
        ) : tab === 'wallets' ? (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setWallet({ open: true, value: null })}>
                <Plus />
                {t('finance.personal.addWallet')}
              </Button>
            </div>
            <WalletsGrid onEdit={(value) => setWallet({ open: true, value })} />
            <SalarySyncCard />
          </div>
        ) : (
          <NotesGrid onEdit={openNote} />
        )}
      </div>

      {entry.open ? <EntryForm key={entry.value?.id ?? 'new'} entry={entry.value} onDone={() => setEntry({ open: false, value: null })} /> : null}
      {note.open ? <NoteForm key={note.value?.id ?? 'new'} note={note.value} onDone={() => setNote({ open: false, value: null })} /> : null}
      {wallet.open ? <WalletForm key={wallet.value?.id ?? 'new'} wallet={wallet.value} onDone={() => setWallet({ open: false, value: null })} /> : null}
    </>
  )
}
